import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/api-client';

interface CharReading {
  char: string;
  tw: string;
  cn: string;
  accepted?: string[];
  note?: string;
}

interface CompoundReading {
  chars: string;
  tw: string[];
  cn: string[];
  accepted?: string[][];
  note?: string;
}

interface PolyReading {
  key: string;
  tw: string;
  cn: string;
  note: string;
}

interface TwReadingsData {
  characters: CharReading[];
  compounds: CompoundReading[];
  polyphones: PolyReading[];
  updatedAt?: string;
}

interface DocResponse {
  content: string;
}

const EMPTY: TwReadingsData = { characters: [], compounds: [], polyphones: [] };

function parseStoredData(raw: string): TwReadingsData {
  try {
    const parsed = JSON.parse(raw);
    return {
      characters: parsed.characters || [],
      compounds: parsed.compounds || [],
      polyphones: parsed.polyphones || [],
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return EMPTY;
  }
}

function serializeData(data: TwReadingsData): string {
  return JSON.stringify({ ...data, updatedAt: new Date().toISOString() }, null, 2);
}

function generateDeployInstructions(data: TwReadingsData): string {
  const charLines = data.characters
    .map(c => `  ${c.char}: { tw: "${c.tw}", cn: "${c.cn}"${c.accepted ? `, accepted: ${JSON.stringify(c.accepted)}` : ''} },`)
    .join('\n');

  const compoundLines = data.compounds
    .map(c => `  { chars: "${c.chars}", tw: ${JSON.stringify(c.tw)}, cn: ${JSON.stringify(c.cn)}${c.accepted ? `, accepted: ${JSON.stringify(c.accepted)}` : ''} },`)
    .join('\n');

  const polyLines = data.polyphones
    .map(p => `  ${p.key}: TW=${p.tw}, CN=${p.cn} — ${p.note}`)
    .join('\n');

  const charJson: Record<string, any> = {};
  for (const c of data.characters) {
    const entry: any = { tw: c.tw, cn: c.cn };
    if (c.accepted) entry.accepted = c.accepted;
    if (c.note) entry._note = c.note;
    charJson[c.char] = entry;
  }

  const compoundJson: Record<string, any> = {};
  for (const c of data.compounds) {
    const entry: any = { tw: c.tw, cn: c.cn };
    if (c.accepted) entry.accepted = c.accepted;
    if (c.note) entry._note = c.note;
    compoundJson[c.chars] = entry;
  }

  const fullJson = JSON.stringify({
    _format: "Taiwan-standard Mandarin readings. Single source of truth.",
    _consumers: ["api/regex-chinese (post-processor)", "scripts/fix-tw-cefr.cjs (CEFR doc converter)"],
    characters: charJson,
    compounds: compoundJson,
    polyphones: Object.fromEntries(data.polyphones.map(p => [p.key, { tw: p.tw, cn: p.cn, _note: p.note }])),
    sandhi: {
      "_note": "Dynamic tone sandhi applied by post-processor.",
      "不_before_tone4": { reading: "bu2", accepted: ["bu4"] },
      "一_before_tone4": { reading: "yi2", accepted: ["yi1"] },
      "一_before_tone123": { reading: "yi4", accepted: ["yi1"] },
    }
  }, null, 2);

  return `# TW Readings Deploy Instructions
# Generated: ${new Date().toISOString()}
# Total: ${data.characters.length} characters, ${data.compounds.length} compounds, ${data.polyphones.length} polyphones

## What to do

Paste this entire file into a Claude Code session in the glossika-content repo. Claude will:
1. Update data/tw-readings.json (single source of truth)
2. Update api/regex-chinese/src/tw-readings.ts (character + compound rules)
3. Update api/regex-chinese/src/pronunciation.ts (system prompt for polyphones)
4. Download ALL CMN_TW CEFR docs from API → run scripts/fix-tw-cefr.cjs → re-upload
5. Deploy the regex-chinese Lambda
6. Re-seed the DynamoDB config store (node scripts/seed-tw-readings.cjs)

## Instructions for Claude

Update the TW readings registry. The canonical data is below.

### Step 1: Write data/tw-readings.json

\`\`\`json
${fullJson}
\`\`\`

### Step 2: Update api/regex-chinese/src/tw-readings.ts

Replace the TW_CHAR_RULES object entries with:
\`\`\`
${charLines}
\`\`\`

Replace the TW_COMPOUND_RULES entries with:
\`\`\`
${compoundLines}
\`\`\`

### Step 3: Update pronunciation.ts system prompt section 4

Polyphone/context-dependent readings (handled by Claude, not post-processor):
\`\`\`
${polyLines}
\`\`\`

### Step 4: Fix all CMN_TW CEFR documents

Download every CMN_TW document from the API, run the fix script, and re-upload.
IMPORTANT: Use Node.js for all operations (never PowerShell for Chinese text).

\`\`\`js
// Run this script with: node fix-all-tw.cjs
const https = require('https');
const fs = require('fs');
const { execSync } = require('child_process');

const API = 'ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';
const LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function apiGet(path) {
  return new Promise((resolve, reject) => {
    https.get('https://' + API + path, (res) => {
      const chunks = [];
      res.on('data', c => chunks.push(c));
      res.on('end', () => {
        if (res.statusCode === 404) return resolve(null);
        if (res.statusCode !== 200) return reject(new Error(path + ': ' + res.statusCode));
        resolve(JSON.parse(Buffer.concat(chunks).toString('utf8')));
      });
    }).on('error', reject);
  });
}

function apiPut(path, body) {
  return new Promise((resolve, reject) => {
    const data = JSON.stringify(body);
    const req = https.request({
      hostname: API, path, method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Content-Length': Buffer.byteLength(data) },
      timeout: 30000
    }, (res) => {
      const chunks = [];
      res.on('data', c => chunks.push(c));
      res.on('end', () => resolve({ status: res.statusCode, body: Buffer.concat(chunks).toString('utf8') }));
    });
    req.on('error', reject);
    req.write(data);
    req.end();
  });
}

(async () => {
  for (const level of LEVELS) {
    const key = level + '_CMN_TW';
    const doc = await apiGet('/api/admin/docs/' + key);
    if (!doc) { console.log(key + ': not found, skipping'); continue; }
    const filePath = '.gen/' + key + '.md';
    fs.writeFileSync(filePath, doc.content, 'utf8');
    const before = Buffer.byteLength(doc.content);
    try {
      execSync('node scripts/fix-tw-cefr.cjs ' + filePath, { stdio: 'inherit' });
    } catch { console.log(key + ': fix script failed'); continue; }
    const fixed = fs.readFileSync(filePath, 'utf8');
    const after = Buffer.byteLength(fixed);
    if (before === after) { console.log(key + ': no changes'); continue; }
    const result = await apiPut('/api/admin/docs/' + key, { content: fixed });
    console.log(key + ': uploaded (' + result.status + ')');
  }
  console.log('Done');
})();
\`\`\`

Or run each level individually:
\`\`\`bash
# Download
node -e "const https=require('https');const fs=require('fs');https.get('https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com/api/admin/docs/B2_CMN_TW',r=>{const c=[];r.on('data',d=>c.push(d));r.on('end',()=>{fs.writeFileSync('.gen/B2_CMN_TW.md',JSON.parse(Buffer.concat(c).toString()).content,'utf8');console.log('downloaded')})})"
# Fix
node scripts/fix-tw-cefr.cjs .gen/B2_CMN_TW.md
# Upload (use the Node.js upload pattern from Step 4 above)
\`\`\`

### Step 5: Deploy regex-chinese Lambda

Commit all changes and push to trigger deploy-regex-chinese.yml workflow.
\`\`\`bash
git add data/tw-readings.json api/regex-chinese/src/tw-readings.ts api/regex-chinese/src/pronunciation.ts
git commit -m "Update TW readings registry"
git push
\`\`\`

### Step 6: Sync DynamoDB config store

After updating data/tw-readings.json, re-seed the admin webapp's backing store:
\`\`\`bash
node scripts/seed-tw-readings.cjs
\`\`\`
`;
}

export default function TwReadings() {
  const [data, setData] = useState<TwReadingsData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [newChar, setNewChar] = useState('');
  const [newTw, setNewTw] = useState('');
  const [newCn, setNewCn] = useState('');
  const [newNote, setNewNote] = useState('');
  const [addMode, setAddMode] = useState<'char' | 'compound' | 'poly'>('char');

  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await apiClient<DocResponse>('/api/admin/docs/_config_tw-readings');
      setData(parseStoredData(resp.content));
      setError(null);
    } catch (err: any) {
      if (err?.status === 404) {
        setData(EMPTY);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (newData: TwReadingsData) => {
    try {
      setSaving(true);
      await apiClient('/api/admin/docs/_config_tw-readings', {
        method: 'PUT',
        body: JSON.stringify({ content: serializeData(newData) }),
      });
      setData(newData);
      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addReading = () => {
    if (!newChar.trim() || !newTw.trim()) return;

    const updated = { ...data };

    if (addMode === 'char') {
      const char = newChar.trim();
      if (char.length !== 1) { setError('Character must be a single character'); return; }
      const existing = updated.characters.findIndex(c => c.char === char);
      const entry: CharReading = { char, tw: newTw.trim(), cn: newCn.trim() || '', note: newNote.trim() || undefined };
      if (existing >= 0) updated.characters[existing] = entry;
      else updated.characters.push(entry);
      updated.characters.sort((a, b) => a.char.localeCompare(b.char));
    } else if (addMode === 'compound') {
      const chars = newChar.trim();
      const tw = newTw.trim().split(/\s+/);
      const cn = newCn.trim().split(/\s+/);
      if (tw.length !== [...chars].length) { setError(`TW readings count (${tw.length}) must match characters (${[...chars].length})`); return; }
      const existing = updated.compounds.findIndex(c => c.chars === chars);
      const entry: CompoundReading = { chars, tw, cn: cn.length === tw.length ? cn : [], note: newNote.trim() || undefined };
      if (existing >= 0) updated.compounds[existing] = entry;
      else updated.compounds.push(entry);
    } else {
      const key = newChar.trim();
      const existing = updated.polyphones.findIndex(p => p.key === key);
      const entry: PolyReading = { key, tw: newTw.trim(), cn: newCn.trim(), note: newNote.trim() };
      if (existing >= 0) updated.polyphones[existing] = entry;
      else updated.polyphones.push(entry);
    }

    save(updated);
    setNewChar(''); setNewTw(''); setNewCn(''); setNewNote('');
    setError(null);
  };

  const deleteChar = (char: string) => {
    const updated = { ...data, characters: data.characters.filter(c => c.char !== char) };
    save(updated);
  };

  const deleteCompound = (chars: string) => {
    const updated = { ...data, compounds: data.compounds.filter(c => c.chars !== chars) };
    save(updated);
  };

  const deletePoly = (key: string) => {
    const updated = { ...data, polyphones: data.polyphones.filter(p => p.key !== key) };
    save(updated);
  };

  const downloadInstructions = () => {
    const text = generateDeployInstructions(data);
    const blob = new Blob([text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tw-readings-deploy-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const seedFromJson = async () => {
    try {
      const resp = await fetch('/tw-readings-seed.json');
      if (!resp.ok) {
        setError('Seed file not found. Upload data/tw-readings.json to seed.');
        return;
      }
      const json = await resp.json();
      const chars: CharReading[] = Object.entries(json.characters || {})
        .filter(([k]) => !k.startsWith('_'))
        .map(([k, v]: [string, any]) => ({
          char: k.replace(/_.*$/, ''),
          tw: v.tw || v,
          cn: v.cn || '',
          note: v._note,
        }));
      const compounds: CompoundReading[] = Object.entries(json.compounds || {})
        .map(([k, v]: [string, any]) => ({
          chars: k,
          tw: v.tw,
          cn: v.cn,
          note: v._note,
        }));
      const polys: PolyReading[] = Object.entries(json.polyphones || {})
        .filter(([k]) => !k.startsWith('_'))
        .map(([k, v]: [string, any]) => ({
          key: k,
          tw: v.tw,
          cn: v.cn,
          note: v._note || '',
        }));
      const newData: TwReadingsData = { characters: chars, compounds, polyphones: polys };
      await save(newData);
      setSuccess(`Seeded ${chars.length} characters, ${compounds.length} compounds, ${polys.length} polyphones`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to seed');
    }
  };

  const filteredChars = filter
    ? data.characters.filter(c => c.char.includes(filter) || c.tw.includes(filter) || c.cn.includes(filter))
    : data.characters;

  if (loading) return <div className="p-4 md:p-8 text-zinc-500">Loading...</div>;

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">TW Readings</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {data.characters.length} characters · {data.compounds.length} compounds · {data.polyphones.length} polyphones
            {data.updatedAt && <span> · Updated {new Date(data.updatedAt).toLocaleDateString()}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={downloadInstructions} className="px-3 py-1.5 text-sm bg-teal-700 hover:bg-teal-600 text-white rounded">
            Download Deploy Instructions
          </button>
          {data.characters.length === 0 && (
            <button onClick={seedFromJson} className="px-3 py-1.5 text-sm bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded">
              Seed from JSON
            </button>
          )}
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded text-red-300 text-sm">{error}</div>}
      {success && <div className="mb-4 p-3 bg-green-900/30 border border-green-800 rounded text-green-300 text-sm">{success}</div>}

      {/* Add new reading */}
      <div className="mb-6 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
        <div className="flex flex-wrap gap-2 mb-3">
          {(['char', 'compound', 'poly'] as const).map(m => (
            <button key={m} onClick={() => setAddMode(m)}
              className={`px-3 py-1 text-sm rounded ${addMode === m ? 'bg-teal-700 text-white' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'}`}>
              {m === 'char' ? 'Character' : m === 'compound' ? 'Compound' : 'Polyphone'}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">
              {addMode === 'char' ? 'Character' : addMode === 'compound' ? 'Compound' : 'Key'}
            </label>
            <input value={newChar} onChange={e => setNewChar(e.target.value)}
              className="w-20 sm:w-28 px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-200 text-lg"
              placeholder={addMode === 'char' ? '亞' : addMode === 'compound' ? '垃圾' : '和_conj'} />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">TW reading</label>
            <input value={newTw} onChange={e => setNewTw(e.target.value)}
              className="w-24 sm:w-32 px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-200"
              placeholder={addMode === 'compound' ? 'le4 se4' : 'ya3'} />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">CN reading <span className="text-zinc-600">(optional)</span></label>
            <input value={newCn} onChange={e => setNewCn(e.target.value)}
              className="w-24 sm:w-32 px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-200"
              placeholder={addMode === 'compound' ? 'la1 ji1' : 'ya4'} />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Note <span className="text-zinc-600">(optional)</span></label>
            <input value={newNote} onChange={e => setNewNote(e.target.value)}
              className="w-full sm:w-48 px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-200"
              placeholder="context or meaning" />
          </div>
          <button onClick={addReading} disabled={saving || !newChar.trim() || !newTw.trim()}
            className="px-4 py-1.5 bg-teal-700 hover:bg-teal-600 disabled:opacity-50 text-white rounded text-sm">
            {saving ? 'Saving...' : 'Add'}
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4">
        <input value={filter} onChange={e => setFilter(e.target.value)}
          className="w-full sm:w-64 px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-zinc-200"
          placeholder="Filter characters..." />
      </div>

      {/* Character rules table */}
      <h2 className="text-lg font-semibold text-zinc-200 mb-2">Character Rules ({filteredChars.length})</h2>
      <div className="overflow-x-auto mb-8">
        <table className="min-w-[500px] w-full text-sm">
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-700">
              <th className="text-left py-2 px-2 w-16">Char</th>
              <th className="text-left py-2 px-2">TW</th>
              <th className="text-left py-2 px-2">CN</th>
              <th className="text-left py-2 px-2">Note</th>
              <th className="py-2 px-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {filteredChars.map(c => (
              <tr key={c.char} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                <td className="py-1.5 px-2 text-xl text-zinc-100">{c.char}</td>
                <td className="py-1.5 px-2 text-teal-400 font-mono">{c.tw}</td>
                <td className="py-1.5 px-2 text-zinc-500 font-mono">{c.cn}</td>
                <td className="py-1.5 px-2 text-zinc-600 text-xs">{c.note || ''}</td>
                <td className="py-1.5 px-2">
                  <button onClick={() => deleteChar(c.char)} className="text-red-600 hover:text-red-400 text-xs">del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Compound rules */}
      <h2 className="text-lg font-semibold text-zinc-200 mb-2">Compound Rules ({data.compounds.length})</h2>
      <div className="overflow-x-auto mb-8">
        <table className="min-w-[500px] w-full text-sm">
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-700">
              <th className="text-left py-2 px-2">Compound</th>
              <th className="text-left py-2 px-2">TW</th>
              <th className="text-left py-2 px-2">CN</th>
              <th className="text-left py-2 px-2">Note</th>
              <th className="py-2 px-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {data.compounds.map(c => (
              <tr key={c.chars} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                <td className="py-1.5 px-2 text-xl text-zinc-100">{c.chars}</td>
                <td className="py-1.5 px-2 text-teal-400 font-mono">{c.tw.join(' ')}</td>
                <td className="py-1.5 px-2 text-zinc-500 font-mono">{c.cn.join(' ')}</td>
                <td className="py-1.5 px-2 text-zinc-600 text-xs">{c.note || ''}</td>
                <td className="py-1.5 px-2">
                  <button onClick={() => deleteCompound(c.chars)} className="text-red-600 hover:text-red-400 text-xs">del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Polyphone notes */}
      <h2 className="text-lg font-semibold text-zinc-200 mb-2">Polyphones ({data.polyphones.length})</h2>
      <p className="text-xs text-zinc-500 mb-2">Context-dependent readings handled by Claude's system prompt, not the post-processor.</p>
      <div className="overflow-x-auto">
        <table className="min-w-[500px] w-full text-sm">
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-700">
              <th className="text-left py-2 px-2">Key</th>
              <th className="text-left py-2 px-2">TW</th>
              <th className="text-left py-2 px-2">CN</th>
              <th className="text-left py-2 px-2">Note</th>
              <th className="py-2 px-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {data.polyphones.map(p => (
              <tr key={p.key} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                <td className="py-1.5 px-2 text-zinc-200 font-mono">{p.key}</td>
                <td className="py-1.5 px-2 text-teal-400 font-mono">{p.tw}</td>
                <td className="py-1.5 px-2 text-zinc-500 font-mono">{p.cn}</td>
                <td className="py-1.5 px-2 text-zinc-600 text-xs">{p.note}</td>
                <td className="py-1.5 px-2">
                  <button onClick={() => deletePoly(p.key)} className="text-red-600 hover:text-red-400 text-xs">del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
